import { useEffect, useState } from "react";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "@/integrations/supabase/client";
import { ServiceIncidentMonitor, type IncidentService, type IncidentSnapshot } from "@/lib/serviceIncidentMonitor";

const messages: Record<IncidentService, string> = {
  auth: "Sign in is temporarily unavailable. Please try again later; other areas may still work.",
  database: "Saved data is temporarily unavailable. Please try again later; other pages may still work.",
  provider: "App building or previews are temporarily unavailable. Please try again later; existing Apps may still run.",
  templates: "Template creation or copying is temporarily unavailable. Please try again later; browsing may still work.",
};

export function ServiceIncidentNotice({ unavailable, backendUnavailable = false }: {
  unavailable: IncidentService[];
  backendUnavailable?: boolean;
}) {
  if (!unavailable.length && !backendUnavailable) return null;
  return <div role="alert" aria-live="assertive" className="sticky top-0 z-[100] border-b-2 border-red-800 bg-red-700 px-4 py-3 text-center font-semibold text-white">
    {backendUnavailable
      ? "Applyt data services are temporarily unavailable. Please try later; other pages may still work."
      : unavailable.map((service) => messages[service]).join(" ")}
  </div>;
}

export function ServiceIncidentBanner() {
  const [status, setStatus] = useState<IncidentSnapshot>({ unavailable: [], backendUnavailable: false });
  useEffect(() => {
    let active = true;
    let inFlight = false;
    const controller = new AbortController();
    const monitor = new ServiceIncidentMonitor({
      supabaseUrl: SUPABASE_URL,
      apiKey: SUPABASE_PUBLISHABLE_KEY,
      siteOrigin: window.location.origin,
      isOnline: () => navigator.onLine,
      isVisible: () => document.visibilityState === "visible",
    });
    const check = async () => {
      if (inFlight || !active) return;
      inFlight = true;
      try {
        const next = await monitor.poll(controller.signal);
        if (active) setStatus(next);
      } finally {
        inFlight = false;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") setStatus(monitor.pause());
      else void check();
    };
    const onOffline = () => setStatus(monitor.pause());
    void check();
    const timer = window.setInterval(() => { void check(); }, 60_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", check);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", check);
    };
  }, []);
  return <ServiceIncidentNotice {...status} />;
}
